import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  can,
  createFix,
  deleteFix,
  fixCandidates,
  getRenderEstimate,
  listFixes,
  listVoices,
  previewFix,
  type Fix,
  type FixKind,
  type FixScope,
} from "../api";
import FixSummary from "../components/FixSummary";
import SheetHeader from "../components/SheetHeader";
import { useT, type Messages } from "../i18n";
import { primaryOrg, useSession } from "../session";

/**
 * CorrectionSheet is where a mispronounced word gets fixed.
 *
 * The shape of it is dictated by one FINDING (requirements/v0-import.md §6):
 * **there is no reliable rule** for which spelling a TTS model reads correctly.
 * Fully vowelling a word fixed some sentences and mangled others. So this is
 * not a form that applies a rule — it offers candidates and renders the
 * sentence with each, because listening is the only test.
 *
 * Two consequences that look like omissions and are not:
 *
 *  - Preview is always an explicit tap, never automatic. Every preview is a
 *    real TTS call against the real budget, so previewing on open, on hover or
 *    on a debounce would spend money the author did not ask to spend.
 *  - Saving does not render. Fixes accumulate and the author regenerates once,
 *    so several corrections in one part cost one part rather than one each.
 *    The chapter is marked as needing a render instead.
 *
 * A word that has been corrected before opens on that correction. It used to
 * open blank, which said the word had never been corrected and offered to
 * correct it again — so the same word collected correction after correction,
 * only one of which was ever heard (ADR-0036).
 */
export default function CorrectionSheet({
  chapterID,
  word,
  sentence,
  onClose,
}: {
  chapterID: string;
  word: string;
  sentence: string;
  onClose: () => void;
}) {
  const { me } = useSession();
  const org = primaryOrg(me);
  const client = useQueryClient();
  const t = useT();

  const [kind, setKind] = useState<FixKind>("replace");
  const [scope, setScope] = useState<FixScope>("chapter");
  const [replacement, setReplacement] = useState("");
  const [hint, setHint] = useState("");
  const [heard, setHeard] = useState<{ url: string; text: string; ipa?: string } | null>(null);

  /**
   * Whether the book's speech model takes hints as IPA (ADR-0035). Read from
   * the estimate the chapter page already holds, which names the model, and
   * the catalogue, which says what the model reads — both cached, so opening
   * the sheet asks for nothing new.
   */
  const estimate = useQuery({
    queryKey: ["estimate", chapterID],
    queryFn: () => getRenderEstimate(chapterID),
    retry: false,
  });
  const catalogue = useQuery({
    queryKey: ["voices"],
    queryFn: listVoices,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const speechModel = catalogue.data?.models?.find((m) => m.name === estimate.data?.model);
  const readsIPA = !!speechModel?.reads_ipa;
  /**
   * A model sent only its text, that does not read IPA either — Soniox today.
   * A hint written for it goes nowhere, so the sheet says so and does not
   * offer the tab: a control that cannot work is worse than no control.
   */
  const hintsIgnored = !!speechModel?.no_direction && !readsIPA;
  const [problem, setProblem] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);

  const candidates = useQuery({
    queryKey: ["fix-candidates", word],
    // Pure logic on the server and no spend, so it is safe to fetch on open.
    queryFn: () => fixCandidates(word),
    staleTime: Infinity,
  });

  /**
   * What is already saved about this word. The server decides what counts as
   * the same word, folding through pnorm, so "the same word" means one thing
   * here and on the render path (ADR-0036).
   *
   * Two answers matter and they are not the same. A `word` match is the
   * correction in force for this word as written. A `variant` is one saved
   * against another spelling — usually the text was edited afterwards — which
   * reaches this chapter and does nothing to this word, because a fix finds its
   * word byte for byte. Showing neither is how the owner's book came to hold a
   * correction per spelling of رمه‌ها.
   */
  const aboutThisWord = useQuery({
    queryKey: ["fixes", chapterID, word],
    queryFn: () => listFixes(chapterID, word),
  });
  const candidatesFor = (match: "word" | "variant") =>
    (aboutThisWord.data?.items ?? []).find(
      (f) => f.match === match && f.scope !== "occurrence" && !f.detached,
    );
  // Narrowest first, which is how the server orders them, so this is the one
  // in force rather than merely the first saved.
  const existing = candidatesFor("word");
  const variant = existing ? undefined : candidatesFor("variant");
  // Whichever is shown is the one Remove would take away.
  const shown = existing ?? variant;
  // An org-scoped correction takes its own permission, so somebody who cannot
  // write one cannot change or remove it either. They can still override it
  // for this chapter, which is what the sheet leaves them able to do.
  const mayEditShown = !!shown && (shown.scope !== "org" || can(org, "fix.write_org"));

  /**
   * Fill the sheet in from the correction in force, once. Not a derived value:
   * the query resolves after the first render, and after that every keystroke
   * is the author's.
   *
   * Only an exact match fills anything in. A correction for another spelling is
   * reported and left alone — it is not what this word says, and writing it
   * into the form would make Save quietly re-save somebody else's decision.
   */
  const prefilled = useRef(false);
  useEffect(() => {
    if (!existing || prefilled.current) return;
    prefilled.current = true;
    setKind(existing.kind);
    if (existing.scope !== "org" || can(org, "fix.write_org")) {
      setScope(existing.scope);
    }
    if (existing.kind === "replace") setReplacement(existing.to_text ?? "");
    else setHint(existing.hint ?? "");
  }, [existing, org]);

  const preview = useMutation({
    mutationFn: (candidate: { kind: FixKind; to?: string; hintText?: string }) =>
      previewFix({
        chapter_id: chapterID,
        sentence,
        kind: candidate.kind,
        from_text: word,
        to_text: candidate.to,
        hint: candidate.hintText,
      }),
    onSuccess: (result) => {
      setProblem(null);
      setHeard({ url: result.url, text: result.text, ipa: result.ipa });
      setRemaining(result.remaining_this_hour);
    },
    onError: (err) => {
      setHeard(null);
      setProblem(messageFor(err, t.common.somethingWrongTryAgain));
    },
  });

  const save = useMutation({
    mutationFn: () =>
      createFix(chapterID, {
        scope,
        kind,
        from_text: word,
        to_text: kind === "replace" ? replacement : undefined,
        hint: kind === "instruct" ? hint : undefined,
      }),
    onSuccess: async () => {
      // The fix list and the staleness badge both change; the audio does not,
      // because saving deliberately starts nothing.
      await client.invalidateQueries({ queryKey: ["fixes", chapterID] });
      await client.invalidateQueries({ queryKey: ["estimate", chapterID] });
      onClose();
    },
    onError: (err) => setProblem(messageFor(err, t.common.somethingWrongTryAgain)),
  });

  const remove = useMutation({
    mutationFn: (fixID: string) => deleteFix(fixID),
    onSuccess: async () => {
      // Removing a correction restores a render key the chapter has held
      // before, so the estimate changes with the list.
      await client.invalidateQueries({ queryKey: ["fixes", chapterID] });
      await client.invalidateQueries({ queryKey: ["estimate", chapterID] });
      onClose();
    },
    onError: (err) => setProblem(messageFor(err, t.common.somethingWrongTryAgain)),
  });

  const chosen = kind === "replace" ? replacement.trim() : hint.trim();
  const canPreview = chosen !== "" && !preview.isPending;

  return (
    <div
      role="dialog"
      aria-label={t.correction.title}
      data-testid="correction-sheet"
      /* A real bottom sheet: a scrolling middle with the actions pinned under
         it. It was a single scrolling box, which on a phone put Listen and Save
         below the fold of their own sheet — reachable only by scrolling past
         the thing you were reading, and on iOS not reachable at all.

         `dvh`, not `vh`: iOS measures `vh` against the viewport with the
         browser chrome hidden, so an 85vh sheet is taller than the screen for
         as long as the chrome is showing. */
      /* z-40 clears both the app header (z-20) and the player's own transport
         bar (z-10). Without it the sheet has no stacking order of its own and
         the transport paints straight over its controls — the Listen button
         ends up underneath the play button. */
      className="fixed inset-x-0 bottom-0 z-40 flex max-h-[85dvh] flex-col border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
    >
      <SheetHeader title={t.correction.title} onClose={onClose} closeTestID="close-sheet" />
      <div className="flex-1 overflow-y-auto p-4">
        {/* dir="auto" goes on this block and on nothing inside it. An element
          with dir="auto" *skips* any descendant carrying its own dir when it
          looks for the first strong directional character, so marking the word
          as well would leave this element with nothing to read and lay a
          Persian sentence out left to right. */}
        <div dir="auto" className="mb-3">
          <p className="text-base font-medium" data-testid="correcting-word">
            {word}
          </p>
          <p className="mt-1 text-sm text-muted" data-testid="correcting-sentence">
            {sentence}
          </p>
        </div>

        {/* What this word is already corrected to, and where that correction
            lives. The sheet is filled in from it, so the author changes one
            correction instead of writing a second one that is never heard. */}
        {existing && (
          <p
            data-testid="existing-fix"
            data-scope={existing.scope}
            className="mb-3 rounded-control border border-line bg-sunk px-3 py-2 text-xs text-ink-soft"
          >
            {t.correction.existing(scopeWord(existing, t))}
          </p>
        )}

        {/* A correction for another spelling of this word: it reaches the
            chapter and does nothing here, which is worth saying plainly — it is
            usually a correction the text was edited past, and the author is
            about to write a second one without knowing. */}
        {variant && (
          <p
            data-testid="variant-fix"
            data-scope={variant.scope}
            className="mb-3 rounded-control border border-line bg-sunk px-3 py-2 text-xs text-ink-soft"
          >
            {t.correction.existingVariant(scopeWord(variant, t))}{" "}
            {/* The words are Persian and carry their own dir; the sentence
                around them is the interface language. dir="auto" therefore goes
                here and not on the paragraph, which would find this span's dir
                and read nothing of its own. */}
            <span dir="auto">
              <FixSummary fix={variant} />
            </span>
          </p>
        )}

        <div className="mb-3 flex gap-2 text-sm">
          <Tab active={kind === "replace"} onClick={() => setKind("replace")} testid="tab-replace">
            {t.correction.respell}
          </Tab>
          <Tab
            active={kind === "instruct"}
            disabled={hintsIgnored}
            onClick={() => setKind("instruct")}
            testid="tab-instruct"
          >
            {t.correction.sayItLike}
          </Tab>
        </div>

        {hintsIgnored && (
          <p className="mb-3 text-xs text-muted" data-testid="hints-unsupported">
            {t.correction.hintsUnsupported(speechModel?.label ?? "")}
          </p>
        )}

        {kind === "replace" ? (
          <>
            {/* The suggestions are Persian shapes and nothing else (fixes.
                Candidates), so a Korean or English word gets an empty list.
                Saying why beats a blank strip where three buttons were, and
                beats what it used to do: hang a Persian kasra on a Hangul
                syllable and draw three empty boxes. */}
            <p className="mb-2 text-xs text-muted">
              {candidates.isSuccess && (candidates.data?.items ?? []).length === 0
                ? t.correction.respellNoSuggestions
                : t.correction.respellNote}
            </p>
            <ul className="mb-3 flex flex-wrap gap-2" data-testid="candidates">
              {(candidates.data?.items ?? []).map((c) => (
                <li key={c.text}>
                  <button
                    type="button"
                    data-testid="candidate"
                    onClick={() => setReplacement(c.text)}
                    aria-pressed={replacement === c.text}
                    title={c.reason}
                    className={`rounded-control border px-2.5 py-1.5 text-sm ${
                      replacement === c.text ? "border-sage bg-sage text-on-sage" : "border-line"
                    }`}
                  >
                    {/* Each candidate is its own block, so dir="auto" here is not
                      nested inside the one above. */}
                    <span dir="auto">{c.text}</span>
                  </button>
                </li>
              ))}
            </ul>
            <input
              dir="auto"
              data-testid="replacement"
              value={replacement}
              onChange={(e) => setReplacement(e.target.value)}
              placeholder={t.correction.typeSpelling}
              className="mb-3 w-full rounded-control border border-line px-3 py-2 text-sm"
            />
          </>
        ) : (
          <>
            <p
              className="mb-2 text-xs text-muted"
              data-testid="hint-note"
              data-reads-ipa={readsIPA ? "true" : "false"}
            >
              {readsIPA ? t.correction.hintNoteIPA : t.correction.hintNote}
            </p>
            <input
              data-testid="hint"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder={t.correction.hintPlaceholder}
              className="mb-3 w-full rounded-control border border-line px-3 py-2 text-sm"
            />
          </>
        )}

        <div className="mb-3 flex items-center gap-2">
          <button
            type="button"
            data-testid="preview"
            disabled={!canPreview}
            onClick={() =>
              preview.mutate(
                kind === "replace"
                  ? { kind, to: replacement.trim() }
                  : { kind, hintText: hint.trim() },
              )
            }
            className="rounded-control border border-line px-3 py-1.5 text-sm disabled:opacity-40"
          >
            {preview.isPending ? t.correction.rendering : t.correction.listen}
          </button>
          {/* The charge is named on the button, not buried in a settings page:
            every tap is a real TTS call against the org's budget. */}
          <span className="text-xs text-muted">
            {t.correction.rendersOne}
            {remaining !== null && t.correction.leftThisHour(remaining)}
          </span>
        </div>

        {heard && (
          <div className="mb-3" data-testid="preview-result">
            <audio
              controls
              autoPlay
              src={heard.url}
              className="w-full"
              data-testid="preview-audio"
            />
            <p dir="auto" className="mt-1 text-xs text-muted">
              {heard.text}
            </p>
            {/* What ElevenLabs was actually sent for the word, so a wrong IPA
                can be seen as well as heard — and typed over between slashes. */}
            {heard.ipa && (
              <p className="mt-1 text-xs text-ink-soft" data-testid="preview-ipa">
                <span dir="ltr">{t.correction.sentAsIPA(heard.ipa)}</span>
              </p>
            )}
          </div>
        )}

        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-xs text-muted">{t.correction.applyTo}</span>
          <select
            data-testid="scope"
            value={scope}
            onChange={(e) => setScope(e.target.value as FixScope)}
            className="w-full rounded-control border border-line px-3 py-2 text-sm"
          >
            <option value="chapter">{t.correction.scopeChapter}</option>
            <option value="book">{t.correction.scopeBook}</option>
            {/* Org scope reaches every book in the library, so it takes its own
              permission (ADR-0009). Offering the option to somebody who cannot
              use it would be a control that only ever produces a 403. */}
            {can(org, "fix.write_org") && <option value="org">{t.correction.scopeOrg}</option>}
          </select>
        </label>

        {problem && (
          <p
            role="alert"
            data-testid="sheet-error"
            className="mb-3 text-sm text-red-600 dark:text-red-400"
          >
            {problem}
          </p>
        )}
      </div>

      {/* Pinned, and padded past the home indicator so Save is not sitting
          under it on a gesture-navigation phone. */}
      <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          data-testid="save-fix"
          disabled={chosen === "" || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-control bg-sage px-3 py-1.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
        >
          {save.isPending
            ? t.correction.saving
            : existing && existing.scope === scope
              ? t.correction.update
              : t.correction.save}
        </button>
        {shown && mayEditShown && (
          <button
            type="button"
            data-testid="remove-fix"
            disabled={remove.isPending}
            onClick={() => remove.mutate(shown.id)}
            className="rounded-control border border-line px-3 py-1.5 text-sm disabled:opacity-40"
          >
            {remove.isPending ? t.correction.removing : t.correction.remove}
          </button>
        )}
        <span className="basis-full text-xs text-muted sm:basis-auto">
          {t.correction.savingNote}
        </span>
      </div>
    </div>
  );
}

function Tab({
  active,
  onClick,
  testid,
  disabled = false,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testid: string;
  /** A tab the book's speech model cannot use. */
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`rounded-control px-3 py-1.5 disabled:opacity-40 ${
        active ? "bg-sage text-on-sage" : "border border-line"
      }`}
    >
      {children}
    </button>
  );
}

/** The scope of an existing correction, as the "Apply to" menu words it. */
function scopeWord(fix: Fix, t: Messages): string {
  switch (fix.scope) {
    case "book":
      return t.correction.scopeBook;
    case "org":
      return t.correction.scopeOrg;
    default:
      return t.correction.scopeChapter;
  }
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.problem.title;
  return fallback;
}
